import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { subscriptionAPI } from '../../services/apiServices';
import { useFetch } from '../../hooks/useFetch';
import { Spinner } from '../../components/common/UI';
import { CreditCardIcon, ShieldIcon, TrendingIcon } from '../../components/common/Icons';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatPlanPrice, getPlanRule, REGIONS } from '../../utils/plans';
import { formatLocalizedDate } from '../../utils/dateUtils';
import { formatMoneyNumber } from '../../utils/currency';

const loadStripeJs = () => new Promise((resolve, reject) => {
  if (window.Stripe) {
    resolve(window.Stripe);
    return;
  }
  const existing = document.querySelector('script[src="https://js.stripe.com/v3"]');
  const script = existing || document.createElement('script');
  script.src = 'https://js.stripe.com/v3';
  script.async = true;
  script.onload = () => resolve(window.Stripe);
  script.onerror = () => reject(new Error('Stripe.js failed to load.'));
  if (!existing) document.head.appendChild(script);
});

const SubscriptionPage = () => {
  const { t, i18n } = useTranslation();
  const { confirm, notify } = useFeedback();
  const { refreshUser } = useAuth();
  const { data: subscription, loading, refetch } = useFetch(() => subscriptionAPI.getMy());
  const [savingPlan, setSavingPlan] = useState(null);
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [embeddedCheckout, setEmbeddedCheckout] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentSetup, setPaymentSetup] = useState(null);
  const completionChecked = useRef(false);
  const language = i18n?.language || 'pt-BR';

  const region = subscription?.region || 'BR';
  const currentPlan = subscription?.plan || 'basic';
  const daysLeft = subscription?.daysLeftInTrial || 0;
  const isTrial = subscription?.isTrialActive;
  const hasActiveAccess = subscription?.accessStatus === 'active';
  const hasFuturePeriod = (() => {
    const value = subscription?.endDate || subscription?.trialEndsAt;
    if (!value) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) && date > new Date();
  })();
  const currentPlanName = currentPlan === 'standard' ? t('plans.standard.title') : t('plans.basic.title');
  const dateLabel = (value) => {
    return formatLocalizedDate(value, language) || t('common.notProvided');
  };

  const plans = useMemo(() => ['basic', 'standard'].map((plan) => getPlanRule(region, plan)), [region]);

  const refreshSubscription = useCallback(async () => {
    setUpgradePreview(null);
    await refetch();
    await refreshUser?.();
  }, [refetch, refreshUser]);

  const loadPaymentMethod = useCallback(async () => {
    if (!subscription?.stripeCustomerId) {
      setPaymentMethod(null);
      return;
    }
    try {
      const { data } = await subscriptionAPI.paymentMethod();
      setPaymentMethod(data?.data?.paymentMethod || null);
    } catch {
      setPaymentMethod(null);
    }
  }, [subscription?.stripeCustomerId]);

  useEffect(() => {
    loadPaymentMethod();
  }, [loadPaymentMethod]);

  useEffect(() => {
    if (completionChecked.current) return;
    completionChecked.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const checkoutStatus = params.get('checkout');
    const upgradeStatus = params.get('upgrade');
    const completed = checkoutStatus === 'success' || upgradeStatus === 'success';
    const cancelled = checkoutStatus === 'cancelled' || upgradeStatus === 'cancelled';
    if (cancelled) {
      notify({
        variant: 'warning',
        title: t('plans.paymentCancelledTitle', 'Pagamento cancelado'),
        message: t('plans.paymentCancelledMessage', 'O pagamento foi cancelado e sua assinatura nao foi alterada.'),
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    if (!sessionId || !completed) return;
    setSavingPlan('checkout-complete');
    subscriptionAPI.completeCheckout({ sessionId })
      .then(async () => {
        await refreshSubscription();
        notify({
          variant: 'success',
          title: t('plans.paymentConfirmedTitle', 'Pagamento confirmado'),
          message: t('plans.paymentConfirmedMessage', 'Sua assinatura foi atualizada.'),
        });
        window.history.replaceState({}, document.title, window.location.pathname);
      })
      .catch((error) => {
        notify({
          variant: 'danger',
          title: t('feedback.planErrorTitle'),
          message: error.response?.data?.error || t('feedback.planErrorMessage'),
        });
      })
      .finally(() => setSavingPlan(null));
  }, [notify, refreshSubscription, t]);

  const scheduleDowngrade = async () => {
    const ok = await confirm({
      variant: 'warning',
      title: t('plans.downgradeTitle'),
      message: t('plans.downgradeMessage', { date: dateLabel(subscription?.endDate || subscription?.trialEndsAt) }),
      confirmText: t('plans.confirmDowngrade'),
    });
    if (!ok) return;

    setSavingPlan('downgrade');
    try {
      await subscriptionAPI.scheduleDowngrade();
      await refreshSubscription();
      notify({
        variant: 'success',
        title: t('plans.downgradeScheduledTitle'),
        message: t('plans.downgradeScheduledMessage'),
      });
    } catch (error) {
      notify({
        variant: 'danger',
        title: t('feedback.planErrorTitle'),
        message: error.response?.data?.error || t('feedback.planErrorMessage'),
      });
    } finally {
      setSavingPlan(null);
    }
  };

  const checkoutPlan = async (plan, preview = null) => {
    const useProratedUpgrade = plan === 'standard' && currentPlan === 'basic' && hasActiveAccess && hasFuturePeriod;
    const action = useProratedUpgrade ? 'upgrade' : `checkout-${plan}`;
    setSavingPlan(action);
    try {
      let response;
      if (action === 'upgrade') {
        response = await subscriptionAPI.upgradeCheckout({
        locale: ['pt-BR', 'en', 'es'].includes(language) ? language : 'en',
          embedded: true,
          returnUrl: `${window.location.origin}/subscription?upgrade=success&session_id={CHECKOUT_SESSION_ID}`,
        });
      } else {
        response = await subscriptionAPI.checkoutSession({
        locale: ['pt-BR', 'en', 'es'].includes(language) ? language : 'en',
          plan,
          region,
          embedded: true,
          returnUrl: `${window.location.origin}/subscription?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        });
      }
      const { data } = response;
      if (data?.data?.existingSubscription) {
        await refreshSubscription();
        notify({
          variant: 'info',
          title: t('plans.existingSubscriptionTitle', 'Assinatura encontrada'),
          message: t('plans.existingSubscriptionMessage', 'Encontramos uma assinatura aberta para este email e sincronizamos sua conta.'),
        });
        return;
      }
      if (data?.data?.clientSecret) {
        setEmbeddedCheckout({ clientSecret: data.data.clientSecret, sessionId: data.data.sessionId });
        return;
      }
      if (data?.data?.url) {
        throw new Error(t('plans.embeddedRequired'));
      }
      if (data?.data?.upgraded) {
        await refreshSubscription();
        notify({
          variant: 'success',
          title: t('plans.upgradeAppliedTitle'),
          message: t('plans.upgradeAppliedMessage'),
        });
        return;
      }
      if (preview) setUpgradePreview(preview);
      throw new Error(t('feedback.planErrorMessage'));
    } catch (error) {
      notify({
        variant: 'danger',
        title: t('feedback.planErrorTitle'),
        message: error.response?.data?.error || t('feedback.planErrorMessage'),
      });
      setSavingPlan(null);
    }
  };

  const previewAndUpgrade = async () => {
    setSavingPlan('preview');
    try {
      const { data } = await subscriptionAPI.upgradePreview();
      const preview = data.data;
      setUpgradePreview(preview);
      const ok = await confirm({
        variant: 'info',
        title: t('plans.upgradePreviewTitle'),
        message: t('plans.upgradePreviewMessage', {
          amount: `${preview.currency} ${formatMoneyNumber(preview.amount, { language })}`,
          date: dateLabel(preview.periodEnd),
        }),
        confirmText: t('plans.confirmUpgrade'),
      });
      if (ok) await checkoutPlan('standard', preview);
    } catch (error) {
      notify({
        variant: 'danger',
        title: t('feedback.planErrorTitle'),
        message: error.response?.data?.error || t('feedback.planErrorMessage'),
      });
    } finally {
      setSavingPlan(null);
    }
  };

  const cancelRenewal = async () => {
    if (!hasFuturePeriod) {
      await subscriptionAPI.cancelRenewal();
      await refreshSubscription();
      return;
    }
    const ok = await confirm({
      variant: 'warning',
      title: t('plans.cancelRenewalTitle'),
      message: t('plans.cancelRenewalMessage', { date: dateLabel(subscription?.endDate || subscription?.trialEndsAt) }),
      confirmText: t('plans.cancelRenewal'),
    });
    if (!ok) return;

    setSavingPlan('cancel-renewal');
    try {
      await subscriptionAPI.cancelRenewal();
      await refreshSubscription();
      notify({
        variant: 'success',
        title: t('plans.cancelRenewalScheduledTitle'),
        message: t('plans.cancelRenewalScheduledMessage'),
      });
    } catch (error) {
      notify({
        variant: 'danger',
        title: t('feedback.planErrorTitle'),
        message: error.response?.data?.error || t('feedback.planErrorMessage'),
      });
    } finally {
      setSavingPlan(null);
    }
  };

  const handlePlanAction = (plan) => {
    if (currentPlan === plan && isTrial) {
      checkoutPlan(plan);
      return;
    }
    if (currentPlan === plan && !hasActiveAccess) {
      checkoutPlan(plan);
      return;
    }
    if (currentPlan === plan) return;
    if (!hasActiveAccess) {
      checkoutPlan(plan);
      return;
    }
    if (currentPlan === 'basic' && plan === 'standard') {
      previewAndUpgrade();
      return;
    }
    if (currentPlan === 'standard' && plan === 'basic') {
      scheduleDowngrade();
      return;
    }
    checkoutPlan(plan);
  };

  const openPaymentMethodSetup = async () => {
    setSavingPlan('payment-method');
    try {
      const { data } = await subscriptionAPI.paymentMethodSetup();
      setPaymentSetup(data?.data);
    } catch (error) {
      notify({
        variant: 'warning',
        title: t('plans.updatePaymentMethod', 'Atualizar pagamento'),
        message: error.response?.data?.error || error.message || t('plans.manageInsideAppMessage'),
      });
    } finally {
      setSavingPlan(null);
    }
  };

  if (loading) return <Spinner size="lg" />;

  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-7">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t('navigation.payments')}</h1>
        <p className="mt-1 text-base text-slate-600">
          {t('plans.subtitle', { days: getPlanRule(region, currentPlan).trialDays })}
        </p>
      </div>

      <section className="mb-6 flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-6 shadow-soft sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
            <TrendingIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{t('profile.mySubscription')}</h2>
            <p className="text-base text-slate-800">
              {t(`location.${region}`, REGIONS[region]?.name || t('location.BR'))} - <span className="font-bold">{currentPlanName}</span>
            </p>
          </div>
        </div>
        <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${isTrial ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {isTrial ? t('profile.trialDaysLeft', { count: daysLeft }) : t(`plans.paymentLabels.${subscription?.paymentStatus || 'paid'}`)}
        </span>
        {subscription?.stripeCustomerId && (
          <button type="button" className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-bold text-slate-800" onClick={openPaymentMethodSetup} disabled={savingPlan === 'payment-method'}>
            {savingPlan === 'payment-method' ? t('common.processing') : t('plans.updatePaymentMethod', 'Atualizar pagamento')}
          </button>
        )}
      </section>
      {subscription?.billingPromotion && (
        <p role="status" className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
          {t(subscription.billingPromotion.status === 'scheduled' ? 'plans.promotionScheduled' : 'plans.promotionApplied')}
        </p>
      )}
      {subscription?.stripeCustomerId && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-soft">
          <span className="font-bold text-slate-900">{t('plans.paymentMethod', 'Forma de pagamento')}: </span>
          {paymentMethod?.last4
            ? `${String(paymentMethod.brand || '').toUpperCase()} **** ${paymentMethod.last4} - ${String(paymentMethod.expMonth || '').padStart(2, '0')}/${paymentMethod.expYear}`
            : t('plans.noPaymentMethod', 'Nenhuma forma de pagamento ativa encontrada.')}
        </section>
      )}

      <p className="mb-4 text-sm text-slate-600">{t('plans.couponCheckoutHint')}</p>
      <section className="mb-6 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-soft md:grid-cols-4">
        <Detail label={t('plans.currentPlan')} value={currentPlanName} />
        <Detail label={t('plans.regionCurrency')} value={`${REGIONS[region]?.name || region} - ${subscription?.currency || plans[0]?.currency}`} />
        <Detail label={t('plans.status')} value={t(`plans.statusLabels.${subscription?.status || 'active'}`)} />
        <Detail label={t('plans.paymentStatus')} value={t(`plans.paymentLabels.${subscription?.paymentStatus || 'trialing'}`)} />
        <Detail label={t('plans.startDate')} value={dateLabel(subscription?.startDate)} />
        <Detail label={t('plans.demoUntilShort')} value={dateLabel(subscription?.trialEndsAt)} />
        <Detail label={t('plans.expiresAt')} value={dateLabel(subscription?.endDate || subscription?.trialEndsAt)} />
        <Detail label={t('plans.price')} value={`${formatPlanPrice(region, currentPlan, language)}${t('plans.month')}`} />
        <Detail label={t('plans.businessLimit')} value={getPlanRule(region, currentPlan).limit} />
        <Detail label={t('plans.accessStatus')} value={t(`plans.accessLabels.${subscription?.accessStatus || 'active'}`)} />
      </section>

      {(subscription?.accessStatus === 'grace' || subscription?.accessStatus === 'locked' || subscription?.pendingPlan || subscription?.cancelAtPeriodEnd) && (
        <section className={`mb-6 rounded-lg border p-4 text-sm shadow-soft ${subscription?.accessStatus === 'locked' ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <p className="font-bold">
            {subscription?.accessStatus === 'locked'
              ? t('plans.lockedTitle')
              : subscription?.accessStatus === 'grace'
                ? t('plans.graceTitle')
                : t('plans.pendingChangeTitle')}
          </p>
          <p className="mt-1">
            {subscription?.accessStatus === 'locked'
              ? t('plans.lockedMessage')
              : subscription?.accessStatus === 'grace'
                ? t('plans.graceMessage', { count: subscription?.daysLeftInGrace || 0 })
                : subscription?.cancelAtPeriodEnd
                  ? t('plans.cancelRenewalPending', { date: dateLabel(subscription?.pendingPlanEffectiveAt) })
                  : t('plans.pendingPlanMessage', { plan: t(`plans.${subscription?.pendingPlan}.title`), date: dateLabel(subscription?.pendingPlanEffectiveAt) })}
          </p>
        </section>
      )}

      {upgradePreview && (
        <section className="mb-6 rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 shadow-soft">
          <p className="font-bold">{t('plans.upgradePreviewTitle')}</p>
          <p className="mt-1">
            {t('plans.upgradePreviewMessage', {
              amount: `${upgradePreview.currency} ${formatMoneyNumber(upgradePreview.amount, { language })}`,
              date: dateLabel(upgradePreview.periodEnd),
            })}
          </p>
        </section>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {plans.map((plan) => (
          (() => {
            const isCurrentPlan = currentPlan === plan.plan;
            const isCurrentPaid = isCurrentPlan && hasActiveAccess && !isTrial;
            const buttonLabel = isCurrentPlan && isTrial
              ? t('plans.payCurrentPlan', 'Pagar plano atual')
              : (hasActiveAccess && currentPlan === 'standard' && plan.plan === 'basic' ? t('plans.scheduleDowngrade') : t('plans.payWithStripe'));
            return (
          <PlanCard
            key={plan.plan}
            active={isCurrentPaid}
            icon={plan.plan === 'basic' ? <TrendingIcon className="h-6 w-6" /> : <ShieldIcon className="h-6 w-6" />}
            iconClass={plan.plan === 'basic' ? 'bg-slate-100 text-slate-700' : 'bg-amber-50 text-amber-700'}
            name={plan.plan === 'basic' ? t('plans.basic.title') : t('plans.standard.title')}
            price={formatPlanPrice(region, plan.plan, language)}
            period={t('plans.month')}
            currentLabel={t('profile.current')}
            features={[
              t('plans.demo', { days: plan.trialDays }),
              t('plans.allFeatures'),
              plan.plan === 'basic' ? t('plans.basic.limit') : t('plans.standard.limit'),
            ]}
            cta={
              isCurrentPaid ? (
                <span className="mt-6 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">{t('profile.current')}</span>
              ) : (
                <>
                <button
                  className="mt-6 inline-flex h-10 w-full items-center justify-center gap-3 rounded-lg bg-sky-500 px-4 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600 disabled:opacity-60"
                  onClick={() => handlePlanAction(plan.plan)}
                  disabled={Boolean(savingPlan)}
                  type="button"
                >
                  <CreditCardIcon className="h-4 w-4" />
                  {savingPlan ? t('common.processing') : buttonLabel}
                </button>
                </>
              )
            }
          />
            );
          })()
        ))}
      </div>

      {!subscription?.cancelAtPeriodEnd && hasFuturePeriod && hasActiveAccess && (
        <div className="mt-6 flex justify-end">
          <button
            className="inline-flex h-10 items-center justify-center rounded-lg border border-rose-200 bg-white px-4 text-sm font-bold text-rose-600 transition hover:bg-rose-50 disabled:opacity-60"
            type="button"
            disabled={Boolean(savingPlan)}
            onClick={cancelRenewal}
          >
            {savingPlan === 'cancel-renewal' ? t('common.processing') : t('plans.cancelRenewal')}
          </button>
        </div>
      )}

      {embeddedCheckout?.clientSecret && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-6">
          <section className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-lg bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">{t('plans.embeddedCheckoutTitle')}</h2>
              <button
                className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-bold text-slate-700"
                type="button"
                onClick={() => {
                  setEmbeddedCheckout(null);
                  setSavingPlan(null);
                  refreshSubscription();
                }}
              >
                {t('common.close', 'Fechar')}
              </button>
            </div>
            <div className="p-5">
              <EmbeddedCheckoutMount
                clientSecret={embeddedCheckout.clientSecret}
                onComplete={async () => {
                  try {
                    await subscriptionAPI.completeCheckout({ sessionId: embeddedCheckout.sessionId });
                    setEmbeddedCheckout(null);
                    setSavingPlan(null);
                    await refreshSubscription();
                    notify({
                      variant: 'success',
                      title: t('plans.paymentConfirmedTitle'),
                      message: t('plans.paymentConfirmedMessage'),
                    });
                  } catch (error) {
                    setSavingPlan(null);
                    notify({
                      variant: 'danger',
                      title: t('feedback.planErrorTitle'),
                      message: error.response?.data?.error || t('feedback.planErrorMessage'),
                    });
                  }
                }}
                onError={(message) => {
                  setSavingPlan(null);
                  notify({ variant: 'danger', title: t('feedback.planErrorTitle'), message });
                }}
              />
            </div>
          </section>
        </div>
      )}
      {paymentSetup?.setupIntent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-6">
          <section className="max-h-[92vh] w-full max-w-lg overflow-auto rounded-lg bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">{t('plans.updatePaymentMethod', 'Atualizar pagamento')}</h2>
              <button
                className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-bold text-slate-700"
                type="button"
                onClick={() => setPaymentSetup(null)}
              >
                {t('common.close', 'Fechar')}
              </button>
            </div>
            <div className="p-5">
              <PaymentMethodSetupMount
                setup={paymentSetup}
                t={t}
                onComplete={async (setupIntentId) => {
                  await subscriptionAPI.completePaymentMethodSetup({ setupIntentId });
                  setPaymentSetup(null);
                  await loadPaymentMethod();
                  notify({
                    variant: 'success',
                    title: t('plans.paymentMethodUpdatedTitle', 'Forma de pagamento atualizada'),
                    message: t('plans.paymentMethodUpdatedMessage', 'A forma de pagamento da sua assinatura foi atualizada.'),
                  });
                }}
                onError={(message) => notify({ variant: 'danger', title: t('feedback.planErrorTitle'), message })}
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 p-3">
    <p className="text-xs font-semibold text-slate-500">{label}</p>
    <p className="mt-1 text-sm font-bold text-slate-900">{value}</p>
  </div>
);

const PlanCard = ({ active, icon, iconClass, name, price, period, features, cta, currentLabel }) => (
  <section className={`relative rounded-lg border bg-white p-6 shadow-soft ${active ? 'border-sky-500 ring-1 ring-sky-500' : 'border-slate-200'}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${iconClass}`}>{icon}</div>
        <div>
          <h2 className="font-bold text-slate-900">{name}</h2>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {price}<span className="text-sm font-medium text-slate-700">{period}</span>
          </p>
        </div>
      </div>
      {active && <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">{currentLabel}</span>}
    </div>
    <ul className="mt-6 space-y-4">
      {features.map((feature) => (
        <li key={feature} className="flex items-center gap-3 text-sm text-slate-700">
          <span className="text-emerald-600">{'\u2713'}</span>
          {feature}
        </li>
      ))}
    </ul>
    {cta}
  </section>
);

const EmbeddedCheckoutMount = ({ clientSecret, onComplete, onError }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    let checkout;
    let destroyed = false;
    loadStripeJs()
      .then((Stripe) => {
        const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (!key) throw new Error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not configured.');
        return Stripe(key);
      })
      .then((stripe) => stripe.initEmbeddedCheckout({ clientSecret, onComplete }))
      .then((instance) => {
        checkout = instance;
        if (!destroyed && containerRef.current) checkout.mount(containerRef.current);
      })
      .catch((error) => onError(error.message));

    return () => {
      destroyed = true;
      checkout?.destroy?.();
    };
  }, [clientSecret, onComplete, onError]);

  return <div ref={containerRef} />;
};

const PaymentMethodSetupMount = ({ setup, t, onComplete, onError }) => {
  const containerRef = useRef(null);
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const paymentElementRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let paymentElement;
    let destroyed = false;
    loadStripeJs()
      .then((Stripe) => {
        const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (!key) throw new Error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not configured.');
        return Stripe(key);
      })
      .then((stripe) => {
        stripeRef.current = stripe;
        const elements = stripe.elements({
          clientSecret: setup.setupIntent,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#0ea5e9',
              colorText: '#0f172a',
              borderRadius: '8px',
              fontFamily: 'Inter, system-ui, sans-serif',
            },
          },
        });
        elementsRef.current = elements;
        paymentElement = elements.create('payment', {
          layout: 'tabs',
        });
        paymentElementRef.current = paymentElement;
        if (!destroyed && containerRef.current) {
          paymentElement.mount(containerRef.current);
          setReady(true);
        }
      })
      .catch((error) => onError(error.message));

    return () => {
      destroyed = true;
      paymentElement?.destroy?.();
      paymentElementRef.current = null;
      elementsRef.current = null;
    };
  }, [onError, setup.setupIntent]);

  const submit = async () => {
    if (!stripeRef.current || !elementsRef.current || !setup?.setupIntent) return;
    setSaving(true);
    try {
      const { error, setupIntent } = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (error) throw new Error(error.message);
      await onComplete(setupIntent.id || setup.setupIntentId);
    } catch (error) {
      onError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('plans.updatePaymentMethodHelp', 'Informe o cartão que será usado nas próximas cobranças da assinatura.')}</p>
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div ref={containerRef} />
      </div>
      <button
        type="button"
        className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-sky-500 px-4 text-sm font-bold text-white disabled:opacity-60"
        disabled={!ready || saving}
        onClick={submit}
      >
        {saving ? t('common.processing') : t('plans.savePaymentMethod', 'Salvar forma de pagamento')}
      </button>
    </div>
  );
};

export default SubscriptionPage;
